import { ASSETS } from "@/lib/constants";
import { computeTotals, money } from "./totals";

const CLINIC = {
  name: "ASHLAN Clinic",
  addressLines: ["48 Cedar Avenue", "Hamilton PM HM 11"],
  phone: "1-441-292-6830",
  fax: "1-441-292-3586",
  email: "info@ashlanclinic.com",
};

export default function InvoicePreview({ draft }) {
  const totals = computeTotals(draft.items, draft.payments);

  return (
    <div
      data-testid="invoice-gen-preview"
      className="bg-white border border-[#E5E7EB] rounded-lg p-8 max-w-3xl text-[#111827]"
    >
      <div className="flex items-start justify-between">
        <img src={ASSETS.ashlanLogo} alt="Ashlan Clinic" className="h-8 w-auto" />
        <div className="text-right text-xs">
          <p className="font-bold">{CLINIC.name}</p>
          <p>{CLINIC.addressLines.join(", ")}</p>
          <p>
            Tel: {CLINIC.phone} Fax: {CLINIC.fax} Email: {CLINIC.email}
          </p>
        </div>
      </div>

      <h2 className="text-3xl text-[#9CA3AF] font-light mt-6">Invoice #{draft.invoice_number}</h2>

      <div className="grid grid-cols-2 gap-6 mt-6 text-sm">
        <div className="space-y-3">
          <div>
            <p className="font-bold">Invoice Date</p>
            <p>{draft.invoice_date || "—"}</p>
          </div>
          <div>
            <p className="font-bold">Status</p>
            <p>{draft.status || "—"}</p>
          </div>
          <div>
            <p className="font-bold">Client</p>
            <p>{draft.bill_to_name || "—"}</p>
            <p>{draft.bill_to_address}</p>
            {draft.bill_to_phone && <p>Tel: {draft.bill_to_phone}</p>}
            {draft.bill_to_email && <p>Email: {draft.bill_to_email}</p>}
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <p className="font-bold">Invoice #</p>
            <p>{draft.invoice_number}</p>
          </div>
          <div>
            <p className="font-bold">Bill To</p>
            <p>{draft.bill_to_name || "—"}</p>
            <p>{draft.bill_to_address}</p>
            {draft.bill_to_phone && <p>Tel: {draft.bill_to_phone}</p>}
            {draft.bill_to_email && <p>Email: {draft.bill_to_email}</p>}
          </div>
          <div>
            <p className="font-bold">Pay To</p>
            <p>{CLINIC.name}</p>
            <p>{CLINIC.addressLines[0]}</p>
            <p>{CLINIC.addressLines[1]}</p>
            <p>Tel: {CLINIC.phone}</p>
            <p>Fax: {CLINIC.fax}</p>
            <p>Email: {CLINIC.email}</p>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-[#4B5563] text-white text-sm font-semibold px-3 py-2">Items</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#E5E7EB]">
            <th className="text-left font-bold py-2">Items</th>
            <th className="text-left font-bold py-2">Details</th>
            <th className="text-left font-bold py-2">Tax</th>
            <th className="text-left font-bold py-2">Total</th>
          </tr>
        </thead>
        <tbody>
          {(draft.items || []).map((it, idx) => (
            <tr key={idx} className="border-b border-[#F3F4F6] align-top">
              <td className="py-3 pr-3">
                <p className="font-bold">
                  {[it.service_date, it.service_time].filter(Boolean).join(" - ")}
                  {it.service_code ? `, ${it.service_code}` : ""} {it.duration ? `(${it.duration})` : ""}
                </p>
                <p className="font-bold">{it.therapist}</p>
              </td>
              <td className="py-3 pr-3 whitespace-pre-line text-[#374151]">{it.notes || "—"}</td>
              <td className="py-3 pr-3">{money(it.tax)}</td>
              <td className="py-3">{money(it.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex justify-end gap-8 border-t border-[#111827] mt-1 pt-2 text-sm font-bold">
        <span>Total</span>
        <span>{money(totals.total)}</span>
      </div>

      <div className="mt-6 bg-[#4B5563] text-white text-sm font-semibold px-3 py-2">Payments</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#E5E7EB]">
            <th className="text-left font-bold py-2">Date</th>
            <th className="text-left font-bold py-2">Details</th>
            <th className="text-left font-bold py-2">Payment Amount</th>
            <th className="text-left font-bold py-2">Payment Applied</th>
          </tr>
        </thead>
        <tbody>
          {(draft.payments || []).length === 0 ? (
            <tr>
              <td colSpan={4} className="py-3 text-[#9CA3AF]">
                No payments recorded
              </td>
            </tr>
          ) : (
            draft.payments.map((p, idx) => (
              <tr key={idx} className="border-b border-[#F3F4F6]">
                <td className="py-2">{p.date || "—"}</td>
                <td className="py-2">{p.details || "—"}</td>
                <td className="py-2">{money(p.amount)}</td>
                <td className="py-2">-{money(p.amount)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="mt-6 bg-[#4B5563] text-white text-sm font-semibold px-3 py-2 flex justify-between">
        <span>Summary</span>
        <span className="text-xs font-normal">As of {new Date().toLocaleString()}</span>
      </div>
      <div className="grid grid-cols-6 gap-2 text-sm mt-2">
        {[
          ["Client Total Invoiced", totals.total],
          ["Client Total Paid", totals.paid],
          ["Client Balance", totals.balance],
          ["Total Invoiced", totals.total],
          ["Total Applied", totals.paid],
          ["Balance", totals.balance],
        ].map(([label, val]) => (
          <div key={label}>
            <p className="font-bold text-xs">{label}</p>
            <p>{money(val)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
